import { api } from '../../services/api';
import { ApiRequestError } from '../../services/apiErrors';
import { getMinimumPrice } from '../../utils/currency';
import { maxPrice } from './priceTest';
import type { WizardData } from './wizardTypes';

/** What the wizard sells: a membership price test, or pay-to-unlock content. */
export type OfferType = 'membership' | 'unlock';

export const UNLOCK_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const UNLOCK_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const UNLOCK_CURRENCY = 'USD';
const VARIANT_NAMES = ['A', 'B', 'C'];

/** The "What to sell" step's form state for an unlock test. */
export interface UnlockDraft {
  file: File | null;
  title: string;
  description: string;
  prices: number[]; // minor units; [0] is the control
  expiresAt: string; // datetime-local value, '' = never
  maxSales: string; // '' = unlimited
}

export function newUnlockDraft(): UnlockDraft {
  return { file: null, title: '', description: '', prices: [1000, 1500], expiresAt: '', maxSales: '' };
}

/** Why the draft can't be created yet, or null when it's ready. */
export function unlockDraftProblem(draft: UnlockDraft): string | null {
  if (!draft.file) return 'Choose an image to sell';
  if (!draft.title.trim()) return 'Give it a title buyers will see';
  const min = getMinimumPrice(UNLOCK_CURRENCY);
  if (draft.prices.length < 2 || draft.prices.some((p) => !Number.isFinite(p) || p < min)) {
    return 'Every price needs to be at least $1.00';
  }
  const max = maxPrice(UNLOCK_CURRENCY);
  if (max !== null && draft.prices.some((p) => p > max)) return "Whop's limit is $2,500.00 a price";
  if (new Set(draft.prices).size !== draft.prices.length) return 'Use a different price each time';
  if (draft.expiresAt && new Date(draft.expiresAt).getTime() <= Date.now()) {
    return 'The stop date has to be in the future';
  }
  if (draft.maxSales && (!/^\d+$/.test(draft.maxSales) || parseInt(draft.maxSales, 10) < 1)) {
    return 'Max sales must be a positive number';
  }
  return null;
}

/** Even split across the prices, remainder on the control (50/50, 34/33/33). */
export function evenSplit(count: number): Record<string, number> {
  const base = Math.floor(100 / count);
  const split: Record<string, number> = {};
  VARIANT_NAMES.slice(0, count).forEach((name, i) => {
    split[name] = base + (i === 0 ? 100 - base * count : 0);
  });
  return split;
}

/**
 * The draft as WizardData, so the shared summary / go-live / checklist
 * components render an unlock test like any other (one-time prices).
 */
export function unlockToWizardData(draft: UnlockDraft, companyId: string): WizardData {
  return {
    companyId,
    productId: '',
    productName: draft.title.trim(),
    basePlanId: '',
    name: draft.title.trim() ? `${draft.title.trim()} — unlock price` : '',
    currency: UNLOCK_CURRENCY.toLowerCase(),
    variants: draft.prices.map((price, i) => ({
      name: VARIANT_NAMES[i],
      price,
      interval: 'one_time',
      trialDays: 0,
    })),
    split: evenSplit(draft.prices.length),
  };
}

/** Steps already done, so a retry after a failure picks up where it stopped. */
export interface UnlockProgress {
  experimentId?: string;
  uploadUrl?: string; // unused so far, and signed for the file it was asked for
  uploadedFile?: File; // the file storage has, until the server has read it
  finalized?: boolean;
}

/** The draft saved but its image didn't get through. The message is for the seller. */
export class UnlockUploadError extends Error {
  constructor(reason: string) {
    super(`Your draft is saved, but its image didn't get through. ${reason} Try again, or choose a different image.`);
  }
}

function apiMessage(err: unknown): string {
  return err instanceof Error && err.message ? err.message : '';
}

/**
 * Create the unlock test (draft + one-time plans), upload the content
 * straight to storage, then finalize (blurred preview). Resumable: pass the
 * progress from a failed attempt, with the same image or another. Each upload
 * URL is used once, since one is signed for a file and only lasts minutes.
 * Returns the experiment id once finalized.
 */
export async function createUnlockTest(
  companyId: string,
  draft: UnlockDraft,
  progress: UnlockProgress,
  onProgress: (next: UnlockProgress) => void
): Promise<string> {
  const file = draft.file;
  if (!file) throw new Error('Choose an image to sell');
  let state = { ...progress };
  const save = (next: UnlockProgress) => {
    state = next;
    onProgress(next);
  };

  if (!state.experimentId) {
    const data = unlockToWizardData(draft, companyId);
    const { experiment, uploadUrl } = await api.createUnlockTest({
      companyId,
      name: data.name,
      title: draft.title.trim(),
      ...(draft.description.trim() && { description: draft.description.trim() }),
      currency: UNLOCK_CURRENCY,
      contentType: file.type,
      contentLength: file.size,
      ...(draft.expiresAt && { expiresAt: new Date(draft.expiresAt).toISOString() }),
      ...(draft.maxSales && { maxSales: parseInt(draft.maxSales, 10) }),
      variants: data.variants.map((v) => ({ name: v.name, price: v.price })),
      split: data.split,
    });
    save({ experimentId: experiment._id, uploadUrl });
  }
  const experimentId = state.experimentId!;
  if (state.uploadedFile !== file) {
    try {
      const url = state.uploadUrl ?? (await api.renewUnlockUpload(experimentId, file.type, file.size));
      await api.uploadPaidPostContent(url, file);
    } catch (err) {
      save({ experimentId });
      throw new UnlockUploadError(apiMessage(err));
    }
    save({ experimentId, uploadedFile: file });
  }
  if (!state.finalized) {
    try {
      await api.finalizeUnlockTest(experimentId);
    } catch (err) {
      // Refused and deleted on the server (422): it needs uploading again.
      if (err instanceof ApiRequestError && err.status === 422) save({ experimentId });
      throw new UnlockUploadError(apiMessage(err));
    }
    save({ ...state, finalized: true });
  }
  return experimentId;
}
